import React, { useRef, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  VybeDealInternal,
  VybePlacedType,
} from "@/vybe-trader-client/trade-history/types";
import DealItem from "./DealItem";
import { useSortDeals } from "@/vybe-trader-client/trade-history/hooks/useSortTrades";
import { useFilterDeals } from "@/vybe-trader-client/trade-history/hooks/useFilterTrades";
import { useFilterStore } from "@/vybe-trader-client/trade-history/stores/useFilterStore";

interface DealsListProps {
  onClick?: (deal: VybeDealInternal) => void;
}

export default function DealsList({ onClick }: DealsListProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  // Get filters from store
  const selectedSymbol = useFilterStore((state) => state.selectedSymbol);
  const dateFilter = useFilterStore((state) => state.dateFilter);
  const customDateFrom = useFilterStore((state) => state.customDateFrom);
  const customDateTo = useFilterStore((state) => state.customDateTo);

  // Use the new filter hook
  const deals = useFilterDeals({
    symbol: selectedSymbol,
    dateFilter,
    customDateFrom,
    customDateTo,
  });

  // Use sorting hook
  const sortedDeals = useSortDeals(deals);

  const virtualizer = useVirtualizer({
    count: sortedDeals.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52, // Height of DealItem
    overscan: 5,
  });

  // Force virtualizer to update when deals change
  useEffect(() => {
    virtualizer.measure();
  }, [sortedDeals.length, virtualizer]);

  const items = virtualizer.getVirtualItems();

  // Function to get the border color based on placed type
  const getBorderColor = (placedType: number) => {
    if (placedType === VybePlacedType.OnSL) {
      return "border-red-500"; // Stop Loss - red
    } else if (placedType === VybePlacedType.OnTP) {
      return "border-green-500"; // Take Profit - green
    }
    return "";
  };

  return (
    <div ref={parentRef} className="h-full overflow-auto">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {items.map((virtualRow) => {
          const deal = sortedDeals[virtualRow.index];
          const borderClass = getBorderColor(deal.placedType);

          return (
            <div
              key={virtualRow.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              className={`${borderClass ? `border-l-4 ${borderClass}` : ""}`}
            >
              <DealItem deal={deal} onClick={() => onClick?.(deal)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
